import { useState, type PointerEvent } from 'react';

/** One sample of a series; a null value leaves a gap in the line. */
export interface SparkPoint {
  at: number;
  value: number | null;
}

const timeFormat = new Intl.DateTimeFormat('pt-BR', { timeStyle: 'medium' });

/**
 * Single-series trend with a hover readout over a window of `slots` samples,
 * the newest on the right; the tile title names the series. `max` fixes the
 * top of the scale (100 for percentages).
 */
export function Sparkline({
  label,
  points,
  slots,
  format,
  max,
}: {
  label: string;
  points: SparkPoint[];
  slots: number;
  format: (v: number) => string;
  max?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const w = 160;
  const h = 24;
  const values = points.flatMap((p) => (p.value === null ? [] : [p.value]));
  if (values.length < 2) {
    return <span className="text-fg-subtle">coletando histórico…</span>;
  }
  const top = Math.max(max ?? 0, ...values, 1);
  const offset = slots - points.length;
  const x = (i: number) => ((i + offset) / (slots - 1)) * w;
  const y = (v: number) => h - 2 - (v / top) * (h - 4);
  // A gap breaks the line into runs; a run of one point is drawn as a dot.
  const runs: { x: number; y: number }[][] = [[]];
  points.forEach((p, i) => {
    if (p.value === null) {
      if ((runs.at(-1) ?? []).length > 0) runs.push([]);
      return;
    }
    runs.at(-1)?.push({ x: x(i), y: y(p.value) });
  });
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left) / r.width) * (slots - 1)) - offset;
    setHover(Math.min(Math.max(i, 0), points.length - 1));
  };
  const hv = hover === null ? undefined : points[hover];
  const hvValue = hv?.value ?? null;
  return (
    <div className="relative">
      <svg
        role="img"
        aria-label={`${label}: mínimo ${format(Math.min(...values))}, máximo ${format(Math.max(...values))}`}
        viewBox={`0 0 ${String(w)} ${String(h)}`}
        preserveAspectRatio="none"
        className="block h-6 w-full max-w-48 touch-none"
        onPointerMove={onMove}
        onPointerLeave={() => {
          setHover(null);
        }}
      >
        {/* Baseline across the whole window. */}
        <line
          x1="0"
          x2={w}
          y1={h - 1}
          y2={h - 1}
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          className="stroke-line"
        />
        {runs.map((r) => {
          const first = r[0];
          if (!first) return null;
          const key = first.x.toFixed(1);
          if (r.length === 1) {
            return <circle key={key} cx={first.x} cy={first.y} r="1.5" className="fill-accent" />;
          }
          return (
            <polyline
              key={key}
              points={r.map((q) => `${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(' ')}
              fill="none"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
              strokeLinecap="round"
              className="stroke-accent"
            />
          );
        })}
        {hover !== null && hvValue !== null && (
          <>
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1="0"
              y2={h}
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
              className="stroke-line-strong"
            />
            <circle
              cx={x(hover)}
              cy={y(hvValue)}
              r="3"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              className="fill-accent stroke-surface"
            />
          </>
        )}
      </svg>
      {hv && hvValue !== null && (
        <div
          role="tooltip"
          className="pointer-events-none absolute top-full left-0 z-10 mt-1 rounded-md border border-line-strong bg-surface-raised px-2 py-1 text-2xs whitespace-nowrap text-fg shadow-lg"
        >
          <span className="font-medium tabular-nums">{format(hvValue)}</span>
          <span className="text-fg-subtle"> · {timeFormat.format(new Date(hv.at))}</span>
        </div>
      )}
    </div>
  );
}
